/* eslint-disable max-len */
import DoneIcon from '@mui/icons-material/Done';
import { Avatar, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import Link from '/@/components/link';
import { LayoutReferences } from '/@/components/refuge/refuge-options';
import { getStaticContent } from '/@/components/refuge/static-content';
import Spinner from '/@/components/spinner';
import Tabs from '/@/components/tabs';
import Title from '/@/components/title';
import { ZoneDisplay } from '/@/components/zone-display';
import { getZoneByDate } from '/@/core/dates';
import settings from '/@/core/settings';
import { useFieldById } from '/@/hooks/fields';
import { _, _lazy } from '/@/locale';
import Select from '/@/v3/components/forms/select';
import styles from './styles.module.css';

@autobind
class Refuge extends Component {
    static label = _lazy('Refugio');
    static icon = 'refuge';

    static propTypes = {
        country: PropTypes.object.isRequired,
        cropConfig: PropTypes.object.isRequired,
        recommendation: PropTypes.object.isRequired,
        productRecommendations: PropTypes.array.isRequired,
        layoutOptions: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string.isRequired,
                description: PropTypes.string.isRequired,
                image: PropTypes.string.isRequired,
            })
        ),
        catalogBrands: PropTypes.object,
    };

    static contextTypes = {
        print: PropTypes.bool,
    };

    state = {
        section: 'refugeOptions',
    };

    UNSAFE_componentWillMount() {
        const contentTypes = this.getContentTypes();
        if (contentTypes.length > 0) this.setState({ section: contentTypes?.[0]?.value });
    }

    getField() {
        return typeof this.props.recommendation.field === 'object' ? this.props.recommendation.field : this.props.field;
    }

    render() {
        return (
            <div className={styles.root}>
                {this.renderStaticContent({ type: 'intro', alwaysVisible: true })}
                {this.renderStaticContent({ type: 'extra', alwaysVisible: true })}
                {this.renderStaticContent({ type: 'extraBR', alwaysVisible: true })}
                {this.renderRIBZone()}
                {this.renderNavigation()}
                {this.renderStaticContent({ type: 'layout-options', title: _('Refugio estructurado') })}
                {this.renderStaticContent({ type: 'rib-layout-options', title: _('Refugio en bolsa (RIB)') })}
                {this.renderStaticContent({ type: 'good-practices', title: _('Buenas prácticas') })}
                {this.renderSelectedProducts()}
                {this.props.country.code === 'BR' &&
                    this.props.brand &&
                    this.props.brand.type === 'corn' &&
                    this.props.catalogBrands &&
                    this.props.catalogBrands.some((brand) => brand.type === 'refuge') &&
                    this.renderCatalogLink()}
            </div>
        );
    }

    renderNavigation() {
        const contentTypes = this.getContentTypes();

        if (this.context.print || contentTypes.length === 0) return null;

        return (
            <div className={styles.navigation}>
                <div className={styles.tabs}>
                    <Tabs
                        theme={settings.app.theme}
                        tabs={contentTypes}
                        selected={this.state.section}
                        onChange={this.handleSectionChange}
                    />
                </div>
                <div className={styles.select}>
                    <Select
                        options={contentTypes}
                        value={this.state.section}
                        onChange={this.handleSectionChange}
                        clearable={false}
                        color={settings.app.theme}
                    />
                </div>
            </div>
        );
    }

    renderStaticContent({ type, title, alwaysVisible }) {
        const content = getStaticContent(type, this.props.cropConfig.crop, this.props.country.code);
        if (!content) return null;
        if (!alwaysVisible && !this.context.print && this.state.section !== type) return null;
        return (
            <Fragment>
                {title && this.context.print && <Title color='light'>{title}</Title>}
                {content.render(this.props.country.code)}
            </Fragment>
        );
    }

    renderRIBZone() {
        if (!getStaticContent('rib-layout-options', this.props.cropConfig.crop, this.props.country.code)) return null;

        const zone = this.getRibZone();
        if (!zone) return null;
        return (
            <div className={classNames(styles.zone, styles[zone.codename])}>
                <ZoneDisplay codename={zone.codename} />
                <div className={styles.content}>
                    <Title>{_(zone.name)}</Title>
                    <p>
                        {zone.recommended
                            ? _('La región de tu lote te permite realizar refugio en bolsa (RIB).')
                            : _(
                                  'La región de tu lote generalmente presenta una alta presión de spodoptera razón por la que no se recomienda hacer refugio en bolsa.'
                              )}
                    </p>
                    <div className={styles.references}>
                        <LayoutReferences
                            items={[
                                { type: 'Rib', name: _('RIB') },
                                { type: 'NotRib', name: _('Refugio estructurado') },
                            ]}
                        />
                    </div>
                </div>
            </div>
        );
    }

    renderSelectedProducts() {
        const hasRibTech = (profile) => profile.technologies.some((tech) => tech && tech.isRib);

        return (
            <div className={styles.products}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>{_('Productos seleccionados')}</th>
                            <th>{_('Productos recomendados para refugio')}</th>
                            {this.props.brand.type !== 'soy' && <th>{_('Versión RIB')}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {this.getProductsWithRefugeOptions().map(({ profile }) => (
                            <tr key={profile.id}>
                                <td>{profile.product.name}</td>
                                <td>{profile.refugeOptions.join(', ')}</td>
                                {this.props.brand.type !== 'soy' && (
                                    <td>
                                        {hasRibTech(profile) ? (
                                            <Avatar
                                                sx={{
                                                    backgroundColor: (t) => `${t.palette.success.main} !important`,
                                                    width: 20,
                                                    height: 20,
                                                    mr: 1,
                                                }}
                                            >
                                                <DoneIcon sx={{ fontSize: 20 }} />
                                            </Avatar>
                                        ) : (
                                            <i className={classNames('material-icons', styles.isNotRib)}>cancel</i>
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    getProductsWithRefugeOptions() {
        return this.props.productRecommendations.filter(
            (productRecommendation) => productRecommendation.profile.refugeOptions.length >= 1
        );
    }

    getContentTypes() {
        const contentTypes = [
            { value: 'layout-options', label: _('Refugio estructurado') },
            { value: 'good-practices', label: _('Buenas prácticas') },
        ];

        // Determine RIB content type placement based on current zone
        const ribZone = this.getRibZone();
        const insertionIndex = ribZone && ribZone.recommended ? 0 : contentTypes.length - 1;
        contentTypes.splice(insertionIndex, 0, { value: 'rib-layout-options', label: _('Refugio en bolsa (RIB)') });

        return contentTypes.filter(({ value }) =>
            Boolean(getStaticContent(value, this.props.cropConfig.crop, this.props.country.code))
        );
    }
    getRibZone() {
        const field = this.getField();
        const { zones } = field.node;
        const { plantingDate } = this.props.cropConfig;

        const zone = getZoneByDate(zones, 'rib', plantingDate);
        if (!zone) return null;

        // TO-DO: Find a better way to determine if rib is recommended or not.
        return { ...zone, recommended: !zone.codename.includes('NO-RIB') };
    }

    handleSectionChange(value) {
        this.setState({ section: value });
    }

    renderCatalogLink() {
        return (
            <Link to='catalog.ProductSelection' params={{ type: 'refuge', slug: 'refugio-max' }}>
                <Button variant='outlined'>{_('Ver todo el catálogo de Refúgio Max')}</Button>
            </Link>
        );
    }
}

const RefugeWrapper = (props: unknown) => {
    const fieldsQuery = useFieldById(
        {
            id: props.recommendation.field,
        },
        {
            enabled: typeof props.recommendation.field !== 'object',
        }
    );
    if (fieldsQuery.isValidating)
        return (
            <Stack justifyContent='center' alignItems='center' my={2}>
                <Spinner></Spinner>;
            </Stack>
        );
    return <Refuge {...props} field={fieldsQuery.data} />;
};

RefugeWrapper.label = _lazy('Refugio');
RefugeWrapper.icon = 'refuge';

export default RefugeWrapper;
